import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Clock, X } from 'lucide-react'
import { Button } from '../../../../components'
import type { AttendanceRecord, AttendanceStatus } from '../types/hrmsAttendanceLeave.types'
import { AttendanceStatusPill } from './AttendanceStatusPill'

interface AttendanceLogDrawerProps {
  record: AttendanceRecord | null
  onClose: () => void
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function formatTime12h(timeStr?: string): string {
  if (!timeStr || timeStr === 'none') return '—'
  const [hStr, mStr] = timeStr.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (isNaN(h) || isNaN(m)) return timeStr
  const period = h >= 12 ? 'PM' : 'AM'
  const displayH = h % 12 === 0 ? 12 : h % 12
  const formattedH = displayH < 10 ? `0${displayH}` : `${displayH}`
  const formattedM = m < 10 ? `0${m}` : `${m}`
  return `${formattedH}:${formattedM} ${period}`
}

function formatWorkingHours(minutes?: number): string {
  if (minutes === undefined || minutes === null) return '—'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

interface AuditDay {
  date: string
  checkIn: string
  checkOut: string
  status: AttendanceStatus
  lateMinutes?: number
}

const RECENT_AUDIT_DAYS: AuditDay[] = [
  { date: 'Sep 23, 2026', checkIn: '09:00 AM', checkOut: '06:00 PM', status: 'Present' },
  { date: 'Sep 22, 2026', checkIn: '09:15 AM', checkOut: '06:05 PM', status: 'Late', lateMinutes: 15 },
  { date: 'Sep 21, 2026', checkIn: '08:58 AM', checkOut: '05:55 PM', status: 'Present' },
  { date: 'Sep 20, 2026', checkIn: '—', checkOut: '—', status: 'On Leave' },
  { date: 'Sep 19, 2026', checkIn: '09:02 AM', checkOut: '06:00 PM', status: 'Present' },
]

export function AttendanceLogDrawer({ record, onClose }: AttendanceLogDrawerProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!record) return

    previousFocusRef.current = document.activeElement as HTMLElement
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const timer = setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 50)

    return () => {
      document.body.style.overflow = originalOverflow
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus()
      }
      clearTimeout(timer)
    }
  }, [record])

  useEffect(() => {
    if (!record) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [record, onClose])

  if (!record) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="attendance-log-drawer-title"
      className="fixed inset-0 z-[100] overflow-x-hidden"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Drawer Panel */}
      <div className="fixed top-0 right-0 z-[101] h-screen w-full max-w-[420px] bg-white shadow-xl rounded-l-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-5 py-4 border-b border-slate-200 flex items-start justify-between shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-[#146971] flex items-center justify-center text-white shrink-0 shadow-xs">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 id="attendance-log-drawer-title" className="text-base font-bold text-slate-900 truncate">
                Attendance Log
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                View daily session timestamps and audit trail.
              </p>
            </div>
          </div>

          <Button
            ref={closeButtonRef}
            variant="secondary"
            size="sm"
            aria-label="Close"
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
            className="h-8 w-8 p-0 flex items-center justify-center border-none bg-transparent hover:bg-slate-200/60 text-slate-500 rounded-lg shrink-0"
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Employee Mini Card */}
          <div className="rounded-xl bg-slate-50 p-3.5 border border-slate-200/80 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[#146971] text-white font-bold flex items-center justify-center shrink-0 text-sm">
                {getInitials(record.name)}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900 text-sm truncate">{record.name}</div>
                <div className="text-xs text-slate-500 truncate">{record.employeeId}</div>
              </div>
            </div>
            <span className="bg-slate-200 text-slate-700 text-[11px] font-semibold px-2.5 py-1 rounded-md shrink-0">
              {record.department}
            </span>
          </div>

          {/* Today's Sessions */}
          <div className="space-y-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-teal-800">
              TODAY'S SESSIONS
            </h3>
            <div className="bg-slate-50 border border-slate-200/70 rounded-lg p-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase">FIRST CHECK-IN</div>
                <div className="text-xs font-semibold text-slate-900 mt-1">
                  {formatTime12h(record.checkIn)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase">LAST PUNCH</div>
                <div className="text-xs font-semibold text-slate-900 mt-1">
                  {formatTime12h(record.checkOut)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase">TOTAL HOURS</div>
                <div className="text-xs font-bold text-[#146971] mt-1">
                  {formatWorkingHours(record.workingMinutes)}
                </div>
              </div>
            </div>
          </div>

          {/* Recent 5 Days Audit */}
          <div className="space-y-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-teal-800">
              RECENT 5 DAYS AUDIT
            </h3>
            <div className="space-y-2">
              {RECENT_AUDIT_DAYS.map((day, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-slate-200 p-3 flex items-center justify-between bg-white shadow-xs"
                >
                  <div>
                    <div className="text-xs font-bold text-slate-900">{day.date}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {day.checkIn} - {day.checkOut}
                    </div>
                  </div>
                  <AttendanceStatusPill status={day.status} lateMinutes={day.lateMinutes} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 border-t border-slate-200 flex justify-end sticky bottom-0 shrink-0">
          <Button
            variant="secondary"
            onClick={onClose}
            className="bg-white hover:bg-slate-100 text-slate-700 border-slate-200 h-9 px-4 text-xs rounded-lg font-semibold shadow-xs"
          >
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
